import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { alertConfigsApi, AlertConfig } from "../../api/client";

interface EmailFormConfig {
  recipients: string[];
}

interface WebhookFormConfig {
  url: string;
  headers: Record<string, string>;
}

interface AlertConfigFormData {
  name: string;
  alert_type: "email" | "webhook";
  config: EmailFormConfig | WebhookFormConfig;
  on_drift: boolean;
  on_scan_failure: boolean;
  is_active: boolean;
}

const emptyEmailConfig: EmailFormConfig = { recipients: [] };

const emptyFormData: AlertConfigFormData = {
  name: "",
  alert_type: "email",
  config: emptyEmailConfig,
  on_drift: true,
  on_scan_failure: true,
  is_active: true,
};

function getEmailRecipients(
  config: EmailFormConfig | WebhookFormConfig,
): string[] {
  return "recipients" in config ? config.recipients : [];
}

function getWebhookUrl(config: EmailFormConfig | WebhookFormConfig): string {
  return "url" in config ? config.url : "";
}

function getWebhookHeaders(
  config: EmailFormConfig | WebhookFormConfig,
): Record<string, string> {
  return "headers" in config ? config.headers : {};
}

export default function AlertConfigsSettings() {
  const queryClient = useQueryClient();
  const [formModal, setFormModal] = useState<{
    open: boolean;
    mode: "create" | "edit";
    config: AlertConfig | null;
  }>({ open: false, mode: "create", config: null });
  const [formData, setFormData] = useState<AlertConfigFormData>(emptyFormData);
  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    config: AlertConfig | null;
  }>({
    open: false,
    config: null,
  });
  const [emailInput, setEmailInput] = useState("");
  const [headerKey, setHeaderKey] = useState("");
  const [headerValue, setHeaderValue] = useState("");

  const { data: alertConfigs, isLoading } = useQuery({
    queryKey: ["alert-configs"],
    queryFn: () => alertConfigsApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data: AlertConfigFormData) => {
      const config =
        data.alert_type === "email"
          ? { recipients: getEmailRecipients(data.config) }
          : {
              url: getWebhookUrl(data.config),
              headers: getWebhookHeaders(data.config),
            };

      return alertConfigsApi.create({
        name: data.name,
        alert_type: data.alert_type,
        config,
        on_drift: data.on_drift,
        on_scan_failure: data.on_scan_failure,
        is_active: data.is_active,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alert-configs"] });
      closeModal();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (params: { id: string; data: AlertConfigFormData }) => {
      const config =
        params.data.alert_type === "email"
          ? { recipients: getEmailRecipients(params.data.config) }
          : {
              url: getWebhookUrl(params.data.config),
              headers: getWebhookHeaders(params.data.config),
            };

      return alertConfigsApi.update(params.id, {
        name: params.data.name,
        alert_type: params.data.alert_type,
        config,
        on_drift: params.data.on_drift,
        on_scan_failure: params.data.on_scan_failure,
        is_active: params.data.is_active,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alert-configs"] });
      closeModal();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => alertConfigsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alert-configs"] });
      setDeleteModal({ open: false, config: null });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (config: AlertConfig) =>
      alertConfigsApi.update(config.id, { is_active: !config.is_active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alert-configs"] });
    },
  });

  const openCreateModal = () => {
    setFormData(emptyFormData);
    setEmailInput("");
    setHeaderKey("");
    setHeaderValue("");
    setFormModal({ open: true, mode: "create", config: null });
  };

  const openEditModal = (config: AlertConfig) => {
    const parsedConfig =
      typeof config.config === "string"
        ? JSON.parse(config.config)
        : config.config;

    setFormData({
      name: config.name,
      alert_type: config.alert_type as "email" | "webhook",
      config: parsedConfig,
      on_drift: config.on_drift,
      on_scan_failure: config.on_scan_failure,
      is_active: config.is_active,
    });
    setEmailInput("");
    setHeaderKey("");
    setHeaderValue("");
    setFormModal({ open: true, mode: "edit", config });
  };

  const closeModal = () => {
    setFormModal({ open: false, mode: "create", config: null });
    setFormData(emptyFormData);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formModal.mode === "create") {
      createMutation.mutate(formData);
    } else if (formModal.config) {
      updateMutation.mutate({ id: formModal.config.id, data: formData });
    }
  };

  const addEmail = () => {
    if (emailInput && emailInput.includes("@")) {
      const currentRecipients = getEmailRecipients(formData.config);
      if (!currentRecipients.includes(emailInput)) {
        setFormData({
          ...formData,
          config: {
            recipients: [...currentRecipients, emailInput],
          },
        });
      }
      setEmailInput("");
    }
  };

  const removeEmail = (email: string) => {
    setFormData({
      ...formData,
      config: {
        recipients: getEmailRecipients(formData.config).filter(
          (e) => e !== email,
        ),
      },
    });
  };

  const addHeader = () => {
    if (headerKey && headerValue) {
      setFormData({
        ...formData,
        config: {
          url: getWebhookUrl(formData.config),
          headers: {
            ...getWebhookHeaders(formData.config),
            [headerKey]: headerValue,
          },
        },
      });
      setHeaderKey("");
      setHeaderValue("");
    }
  };

  const removeHeader = (key: string) => {
    const newHeaders = { ...getWebhookHeaders(formData.config) };
    delete newHeaders[key];
    setFormData({
      ...formData,
      config: {
        url: getWebhookUrl(formData.config),
        headers: newHeaders,
      },
    });
  };

  const handleAlertTypeChange = (type: "email" | "webhook") => {
    setFormData({
      ...formData,
      alert_type: type,
      config: type === "email" ? { recipients: [] } : { url: "", headers: {} },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Alert Configurations
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure email and webhook alerts for drift events and scan failures
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
        >
          Add Alert Config
        </button>
      </div>

      {/* Alert Configs List */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Triggers
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Configuration
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-12 text-center text-gray-500"
                >
                  Loading alert configurations...
                </td>
              </tr>
            ) : !alertConfigs?.alert_configs ||
              alertConfigs.alert_configs.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-12 text-center text-gray-500"
                >
                  No alert configurations found. Create one to get notified
                  about drift events.
                </td>
              </tr>
            ) : (
              alertConfigs.alert_configs.map((config) => {
                const parsedConfig =
                  typeof config.config === "string"
                    ? JSON.parse(config.config)
                    : config.config;

                return (
                  <tr key={config.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {config.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          config.alert_type === "email"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-purple-100 text-purple-800"
                        }`}
                      >
                        {config.alert_type === "email" ? "Email" : "Webhook"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {config.on_drift && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                            Drift
                          </span>
                        )}
                        {config.on_scan_failure && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
                            Scan Failure
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600 max-w-xs truncate">
                        {config.alert_type === "email" ? (
                          <span>
                            {parsedConfig.recipients?.length || 0} recipient(s)
                          </span>
                        ) : (
                          <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">
                            {parsedConfig.url || "No URL"}
                          </code>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleActiveMutation.mutate(config)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          config.is_active ? "bg-blue-600" : "bg-gray-200"
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            config.is_active ? "translate-x-5" : "translate-x-0"
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => openEditModal(config)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setDeleteModal({ open: true, config })}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {formModal.open && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit}>
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  {formModal.mode === "create"
                    ? "Create Alert Configuration"
                    : "Edit Alert Configuration"}
                </h3>
              </div>
              <div className="px-6 py-4 space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                    placeholder="e.g., Production Alerts"
                  />
                </div>

                {/* Alert Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alert Type
                  </label>
                  <div className="flex space-x-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        checked={formData.alert_type === "email"}
                        onChange={() => handleAlertTypeChange("email")}
                        className="form-radio h-4 w-4 text-blue-600"
                      />
                      <span className="ml-2 text-sm text-gray-700">Email</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        checked={formData.alert_type === "webhook"}
                        onChange={() => handleAlertTypeChange("webhook")}
                        className="form-radio h-4 w-4 text-blue-600"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Webhook
                      </span>
                    </label>
                  </div>
                </div>

                {/* Email Configuration */}
                {formData.alert_type === "email" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Recipients
                    </label>
                    <div className="flex space-x-2 mb-2">
                      <input
                        type="email"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addEmail();
                          }
                        }}
                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                        placeholder="email@example.com"
                      />
                      <button
                        type="button"
                        onClick={addEmail}
                        className="px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
                      >
                        Add
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {getEmailRecipients(formData.config).map((email) => (
                        <span
                          key={email}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {email}
                          <button
                            type="button"
                            onClick={() => removeEmail(email)}
                            className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-gray-400 hover:text-gray-600"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Webhook Configuration */}
                {formData.alert_type === "webhook" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Webhook URL
                      </label>
                      <input
                        type="url"
                        value={getWebhookUrl(formData.config)}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            config: {
                              url: e.target.value,
                              headers: getWebhookHeaders(formData.config),
                            },
                          })
                        }
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                        placeholder="https://hooks.slack.com/..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Headers (optional)
                      </label>
                      <div className="flex space-x-2 mb-2">
                        <input
                          type="text"
                          value={headerKey}
                          onChange={(e) => setHeaderKey(e.target.value)}
                          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                          placeholder="Header name"
                        />
                        <input
                          type="text"
                          value={headerValue}
                          onChange={(e) => setHeaderValue(e.target.value)}
                          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                          placeholder="Header value"
                        />
                        <button
                          type="button"
                          onClick={addHeader}
                          className="px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
                        >
                          Add
                        </button>
                      </div>
                      <div className="space-y-1">
                        {Object.entries(getWebhookHeaders(formData.config)).map(
                          ([key, value]) => (
                            <div
                              key={key}
                              className="flex items-center justify-between bg-gray-50 px-3 py-1.5 rounded text-sm"
                            >
                              <span>
                                <span className="font-medium">{key}:</span>{" "}
                                {value}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeHeader(key)}
                                className="text-red-500 hover:text-red-700"
                              >
                                &times;
                              </button>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Trigger Options */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trigger On
                  </label>
                  <div className="space-y-2">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.on_drift}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            on_drift: e.target.checked,
                          })
                        }
                        className="form-checkbox h-4 w-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Drift Detection
                      </span>
                    </label>
                    <br />
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.on_scan_failure}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            on_scan_failure: e.target.checked,
                          })
                        }
                        className="form-checkbox h-4 w-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Scan Failure
                      </span>
                    </label>
                  </div>
                </div>

                {/* Active Toggle */}
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">
                    Active
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        is_active: !formData.is_active,
                      })
                    }
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      formData.is_active ? "bg-blue-600" : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        formData.is_active ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? "Saving..."
                    : formModal.mode === "create"
                      ? "Create"
                      : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.open && deleteModal.config && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4">
              <h3 className="text-lg font-medium text-gray-900">
                Delete Alert Configuration
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                Are you sure you want to delete "{deleteModal.config.name}"?
                This action cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setDeleteModal({ open: false, config: null })}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteModal.config!.id)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
